import {equals, isNil, isNotNil} from 'ramda';

import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {assignSafely} from 'shared/web';

// Both sourcing user-list endpoints use the same server page size and boundary logic.
// Keep these implementation helpers private to api/core rather than exporting them.
import {ROWS_PER_PAGE} from '../sourcing/constants/rowsPerPage';
import {CreateUserVehicleSummaryRequestBody, PushVehiclesToSaleApiArg} from '../sourcing/types';
import {getInfiniteScrollBoundaryOffset} from '../sourcing/utils/getInfiniteScrollBoundaryOffset';
import {hasMoreUserListPages} from '../sourcing/utils/hasMoreUserListPages';
import {List, OmneticMutationLifecycleApi} from '../sourcing/utils/sourcingApiHelpers';
import {updateListEndReached} from '../sourcing/utils/updateListEndReached';
import {updateScrollAttemptedOffset} from '../sourcing/utils/updateScrollAttemptedOffset';
import {
  AuctionServerResponseBody,
  AuctionUserListQuery,
  AuctionVehicleDetailResponseBody,
  AuctionCacheApi,
  AuctionPricingContext,
  AuctionVehicleResponseBody,
  CachedAuctionArgs,
  CreateAuctionUserSeenApiArg,
  CreateAuctionUserSeenApiResponse,
  ExternalIdWithPositionRequestBody,
  GetAuctionVehicleCountApiArg,
  GetAuctionVehicleCountApiResponse,
  GetAuctionVehicleDetailApiArg,
  GetAuctionVehicleDetailApiResponse,
  GetAuctionVehicleListApiArg,
  GetAuctionVehicleListApiResponse,
  ListAuctionVehiclesByIdsApiArg,
  ListAuctionVehiclesByIdsApiResponse,
  ListAuctionUserResponseBody,
  SortAuctionUserComparisonRequestApiArg,
} from './types';
import {filterOutAuctionDuplicates} from './utils/filterOutAuctionDuplicates';
import {isAuctionListResponse} from './utils/isAuctionListResponse';
import {normalizeAuctionCurrencies} from './utils/normalizeAuctionCurrencies';
import {normalizeAuctionVehicleCurrencies} from './utils/normalizeAuctionVehicleCurrencies';

type AuctionBulkUserActionApiArg = {
  body: {externalIds: string[]};
};

const AUCTION_USER_LIST_TAGS = {
  'user-comparison': 'SourcingAuctionComparisonList',
  'user-favourite': 'SourcingAuctionFavouriteList',
  'user-hidden': 'SourcingAuctionHiddenList',
  'user-to-buy': 'SourcingAuctionToBuyList',
} as const;

type AuctionListEndpointName = 'getSourcingAuctionList';

const getOffset = (page: number = 1) => (page - 1) * ROWS_PER_PAGE;

function isAuctionListEndpointName(endpointName: string): endpointName is AuctionListEndpointName {
  return endpointName === 'getSourcingAuctionList';
}

const sourcingAuctionApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Accumulating list query for infinite scroll. All pages for a given (filters, currency,
     * orderBy) tuple merge into a single cache entry via `merge` (append). Real-time polling
     * never adds rows here; it only overwrites `auctionVehicle` of loaded rows (see
     * `listAuctionVehiclesByIds`).
     */
    getSourcingAuctionList: build.query<
      GetAuctionVehicleListApiResponse,
      GetAuctionVehicleListApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle',
        method: 'POST',
        body: queryArg.body ?? {},
        params: {
          offset: getOffset(queryArg.page),
          size: ROWS_PER_PAGE,
          currency: queryArg.currency,
          pricingStrategy: queryArg.pricingStrategy,
          'orderBy[]': queryArg.orderBy,
        },
      }),
      transformResponse: (rawData: GetAuctionVehicleListApiResponse, _meta, queryArg) => {
        const response = {
          ...rawData,
          data: rawData.data.map(normalizeAuctionCurrencies),
        };
        updateScrollAttemptedOffset(response, queryArg.page);
        updateListEndReached(response, response.data.length);
        return response;
      },
      serializeQueryArgs: ({endpointName, queryArgs}) =>
        // Page is intentionally NOT in the cache key — all pages for the same filter set
        // accumulate into one cache entry via `merge` (append).
        `${endpointName}_${queryArgs.currency}_${queryArgs.pricingStrategy}_${queryArgs.orderBy}_${JSON.stringify(queryArgs.body)}`,
      merge: (currentCache, newData, meta) => {
        const newVehicles = filterOutAuctionDuplicates(currentCache.data, newData.data);
        currentCache.data.push(...newVehicles);
        if (currentCache.links) {
          currentCache.links.size = currentCache.data.length;
        }
        updateScrollAttemptedOffset(currentCache, meta.arg.page);
        updateListEndReached(currentCache, newData.data.length);
      },
      forceRefetch: (params) => {
        // Mirror of sourcing/api.ts forceRefetch. See README §Infinite scroll.
        const currentPage = params.currentArg?.page ?? 1;
        const previousPage = params.previousArg?.page;
        const requestedOffset = getOffset(currentPage);
        const endpointData = params.endpointState?.data;
        const cachedLength = isAuctionListResponse(endpointData) ? endpointData.data.length : 0;
        const scrollAttemptedOffset = isAuctionListResponse(endpointData)
          ? endpointData.scrollAttemptedOffset
          : undefined;
        const cachedPageOffset = getInfiniteScrollBoundaryOffset(
          cachedLength,
          scrollAttemptedOffset
        );
        const shouldRefetch =
          requestedOffset === cachedPageOffset ||
          (requestedOffset > cachedPageOffset && currentPage === (previousPage ?? 1) + 1);

        return shouldRefetch;
      },
      providesTags: ['SourcingAuctionList', 'SourcingAuctionAllListsWithSameResponseStructure'],
      keepUnusedDataFor: 1800,
    }),
    /**
     * Fresh Alpha data for already loaded auctions, used by the All Offers real-time poll.
     * The hook writes the result into the other caches via `patchAuctionVehiclesInCaches`.
     * Each `adIds` batch is its own cache key and callers always forceRefetch, so entries
     * expire as soon as the poll unsubscribes (`keepUnusedDataFor: 0`).
     */
    listAuctionVehiclesByIds: build.query<
      ListAuctionVehiclesByIdsApiResponse,
      ListAuctionVehiclesByIdsApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle/list-by-ids',
        method: 'POST',
        body: queryArg.body,
        params: {
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          pricingStrategy: queryArg.pricingStrategy,
        },
      }),
      transformResponse: (response: ListAuctionVehiclesByIdsApiResponse) => ({
        data: response.data.map(normalizeAuctionVehicleCurrencies),
      }),
      keepUnusedDataFor: 0,
    }),

    getSourcingAuctionDetail: build.query<
      GetAuctionVehicleDetailApiResponse,
      GetAuctionVehicleDetailApiArg
    >({
      query: (arg) => ({
        url: `/dms/v1/auction-vehicle/${arg.externalId}`,
        method: 'GET',
        params: {
          margin: arg.margin,
          currency: arg.currency,
          buyerCountry: arg.buyerCountry,
          pricingStrategy: arg.pricingStrategy,
        },
      }),
      transformResponse: (response: GetAuctionVehicleDetailApiResponse) =>
        normalizeAuctionCurrencies(response),
      providesTags: (_result, _error, arg) => [{type: 'SourcingAuctionDetail', id: arg.externalId}],
    }),

    getSourcingAuctionCount: build.query<
      GetAuctionVehicleCountApiResponse,
      GetAuctionVehicleCountApiArg
    >({
      query: (arg) => ({
        url: '/dms/v1/auction-vehicle/_count',
        method: 'POST',
        body: arg.body ?? {},
        params: {
          currency: arg.currency,
          pricingStrategy: arg.pricingStrategy,
        },
      }),
      providesTags: ['SourcingAuctionList'],
    }),

    getAuctionUserList: build.infiniteQuery<
      ListAuctionUserResponseBody,
      AuctionUserListQuery,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          hasMoreUserListPages(lastPage) ? lastPageParam + ROWS_PER_PAGE : undefined,
      },
      query: ({queryArg: {list, orderBy, ...params}, pageParam}) => ({
        url: `/dms/v1/auction-vehicle/${list}`,
        params: {...params, 'orderBy[]': orderBy, size: ROWS_PER_PAGE, offset: pageParam},
      }),
      transformResponse: (response: ListAuctionUserResponseBody) => ({
        ...response,
        data: response.data.map(normalizeAuctionCurrencies),
      }),
      providesTags: (_result, _error, {list}) => [
        'SourcingAuctionAllListsWithSameResponseStructure',
        AUCTION_USER_LIST_TAGS[list],
      ],
    }),
    getAuctionUserToBuyPage: build.query<
      ListAuctionUserResponseBody,
      Omit<AuctionUserListQuery, 'list'> & {offset: number}
    >({
      query: ({orderBy, ...params}) => ({
        url: '/dms/v1/auction-vehicle/user-to-buy',
        params: {...params, 'orderBy[]': orderBy, size: ROWS_PER_PAGE},
      }),
      transformResponse: (response: ListAuctionUserResponseBody) => ({
        ...response,
        data: response.data.map(normalizeAuctionCurrencies),
      }),
      keepUnusedDataFor: 0,
    }),
    auctionBulkDeleteUserFavourite: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-favourite/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'user-favourite');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'favourite', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'favourite', false);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionFavouriteList',
      ],
    }),

    auctionBulkCreateUserFavourite: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-favourite/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'favourite', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'favourite', true);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionFavouriteList',
      ],
    }),

    auctionBulkDeleteUserComparison: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-comparison/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'user-comparison');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'comparison', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'comparison', false);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionComparisonList',
      ],
    }),
    auctionClearUserComparison: build.mutation<void, void>({
      query: () => ({
        url: '/dms/v1/auction-vehicle/user-comparison',
        method: 'DELETE',
      }),
      onQueryStarted(_args, mutationLifecycleApi) {
        sourcingAuctionApi.util
          .selectInvalidatedBy(mutationLifecycleApi.getState(), [
            {type: 'SourcingAuctionComparisonList'},
          ])
          .forEach(({endpointName, originalArgs}) => {
            if (endpointName !== 'getAuctionUserList' || originalArgs.list !== 'user-comparison') {
              return;
            }
            const patch = mutationLifecycleApi.dispatch(
              sourcingAuctionApi.util.updateQueryData(
                'getAuctionUserList',
                originalArgs,
                (draft) => {
                  draft.pages.splice(1);
                  draft.pageParams.splice(1);
                  draft.pages.forEach((page) => {
                    page.data = [];
                    page.links.totalCount = 0;
                    page.links.hasMore = false;
                  });
                }
              )
            );
            mutationLifecycleApi.queryFulfilled.catch(patch.undo);
          });
        optimisticClearComparisonFlags(mutationLifecycleApi);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionComparisonList',
      ],
    }),

    auctionBulkCreateUserComparison: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-comparison/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'comparison', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'comparison', true);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionComparisonList',
      ],
    }),

    auctionReorderUserComparison: build.mutation<void, SortAuctionUserComparisonRequestApiArg>({
      query: (queryArg) => {
        const auctionVehiclesWithPositions: ExternalIdWithPositionRequestBody[] = queryArg.map(
          (vehicle, index) => ({
            externalId: vehicle.auctionVehicle.adId ?? '',
            position: index + 1,
          })
        );

        return {
          url: '/dms/v1/auction-vehicle/user-comparison/sort',
          method: 'POST',
          body: {auctionVehiclesWithPositions},
        };
      },
      onQueryStarted(patch, mutationLifecycleApi) {
        sourcingAuctionApi.util
          .selectInvalidatedBy(mutationLifecycleApi.getState(), [
            {type: 'SourcingAuctionComparisonList'},
          ])
          .forEach(({endpointName, originalArgs}) => {
            if (endpointName === 'getAuctionUserList') {
              if (originalArgs.list !== 'user-comparison') {
                return;
              }
              const patchResult = mutationLifecycleApi.dispatch(
                sourcingAuctionApi.util.updateQueryData(
                  'getAuctionUserList',
                  originalArgs,
                  (draft) => {
                    const loaded = draft.pages.flatMap((page) => page.data);
                    const byId = new Map(loaded.map((row) => [row.auctionVehicle.adId, row]));
                    if (
                      loaded.length !== patch.length ||
                      byId.size !== patch.length ||
                      patch.some((row) => !byId.has(row.auctionVehicle.adId))
                    ) {
                      return;
                    }
                    let index = 0;
                    draft.pages.forEach((page) => {
                      const length = page.data.length;
                      page.data = patch
                        .slice(index, index + length)
                        .map((row) => byId.get(row.auctionVehicle.adId))
                        .filter(isNotNil);
                      index += length;
                    });
                  }
                )
              );
              mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
              return;
            }
          });
      },
      invalidatesTags: [{type: 'SourcingAuctionComparisonList'}],
    }),

    auctionBulkDeleteUserHidden: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-hidden/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'user-hidden');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'hidden', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'hidden', false);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionHiddenList',
      ],
    }),

    auctionBulkCreateUserHidden: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-hidden/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'getSourcingAuctionList');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'hidden', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'hidden', true);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionHiddenList',
      ],
    }),

    auctionBulkDeleteUserToBuy: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-to-buy/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'user-to-buy');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'inCart', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'inCart', false);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionToBuyList',
      ],
    }),

    auctionBulkCreateUserToBuy: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-to-buy/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'inCart', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'inCart', true);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionToBuyList',
      ],
    }),

    // The backend drops bought vehicles from the to-buy list itself, so unlike the classifieds
    // `sourcingPushVehiclesToSale` no bulk delete follows; a failed buy undoes the patches.
    auctionPushVehiclesToSale: build.mutation<void, PushVehiclesToSaleApiArg>({
      query: ({externalIds}) => ({
        url: '/dms/v1/auction-vehicle/user-to-buy/buy-vehicles',
        method: 'POST',
        body: {auctionVehiclesToBuy: externalIds},
      }),
      onQueryStarted({externalIds}, mutationLifecycleApi) {
        const patchArgs = {body: {externalIds}};
        optimisticRemoveVehiclesFromList(patchArgs, mutationLifecycleApi, 'user-to-buy');
        optimisticUpdateVehicleListCaches(patchArgs, mutationLifecycleApi, 'inCart', false);
        optimisticUpdateVehicleDetailCache(patchArgs, mutationLifecycleApi, 'inCart', false);
      },
      invalidatesTags: invalidateOnSuccess([
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionToBuyList',
        'SourcingAuctionFavouriteList',
        'SourcingAuctionComparisonList',
        'SourcingAuctionHiddenList',
      ]),
    }),

    sourcingSetAuctionUserSeen: build.mutation<
      CreateAuctionUserSeenApiResponse,
      CreateAuctionUserSeenApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-seen`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'seen',
          true
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'seen',
          true
        );
      },
    }),

    getAuctionServerList: build.query<AuctionServerResponseBody[], void>({
      query: () => ({url: '/dms/v1/sourcing/auction-server'}),
    }),
  }),
});

function optimisticClearComparisonFlags(mutationLifecycleApi: OmneticMutationLifecycleApi<void>) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAuctionAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName === 'getAuctionUserList') {
        const patch = mutationLifecycleApi.dispatch(
          sourcingAuctionApi.util.updateQueryData('getAuctionUserList', originalArgs, (draft) => {
            draft.pages.forEach((page) =>
              page.data.forEach((vehicle) => assignSafely(vehicle, 'comparison', false))
            );
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patch.undo);
      } else if (isAuctionListEndpointName(endpointName)) {
        const patch = mutationLifecycleApi.dispatch(
          sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            draft.data.forEach((vehicle) => assignSafely(vehicle, 'comparison', false));
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patch.undo);
      }
    });
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingAuctionDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'getSourcingAuctionDetail') {
        return;
      }
      const patch = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          assignSafely(draft, 'comparison', false);
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patch.undo);
    });
}

function optimisticUpdateVehicleDetailCache<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  list: List,
  isInList: boolean
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingAuctionDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'getSourcingAuctionDetail') {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      if (queryArgs.body.externalIds.includes(originalArgs.externalId)) {
        const patchResult = mutationLifecycleApi.dispatch(
          sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            assignSafely(draft, list, isInList);
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
      }
    });
}

function optimisticUpdateVehicleListCaches<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  list: List,
  isInList: boolean
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAuctionAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName === 'getAuctionUserList') {
        const patch = mutationLifecycleApi.dispatch(
          sourcingAuctionApi.util.updateQueryData('getAuctionUserList', originalArgs, (draft) => {
            draft.pages.forEach((page) =>
              page.data.forEach((vehicle) => {
                const adId = vehicle.auctionVehicle.adId;
                if (isNotNil(adId) && queryArgs.body.externalIds.includes(adId)) {
                  assignSafely(vehicle, list, isInList);
                }
              })
            );
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patch.undo);
        return;
      }
      if (!isAuctionListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.data.forEach((vehicle) => {
            const adId = vehicle.auctionVehicle.adId;
            if (isNotNil(adId) && queryArgs.body.externalIds.includes(adId)) {
              assignSafely(vehicle, list, isInList);
            }
          });
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

function optimisticRemoveVehiclesFromList<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  listToRemove?: AuctionUserListQuery['list'] | 'getSourcingAuctionList'
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAuctionAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName === 'getAuctionUserList') {
        if (isNotNil(listToRemove) && originalArgs.list !== listToRemove) {
          return;
        }
        const patch = mutationLifecycleApi.dispatch(
          sourcingAuctionApi.util.updateQueryData('getAuctionUserList', originalArgs, (draft) => {
            const removed = new Set<string>();
            draft.pages.forEach((page) => {
              page.data = page.data.filter((vehicle) => {
                const adId = vehicle.auctionVehicle.adId;
                if (isNil(adId) || !queryArgs.body.externalIds.includes(adId)) {
                  return true;
                }
                removed.add(adId);
                return false;
              });
            });
            draft.pages.forEach((page) => {
              if (isNotNil(page.links.totalCount)) {
                page.links.totalCount = Math.max(0, page.links.totalCount - removed.size);
              }
              if (removed.size > 0) {
                // Keep paging stopped until invalidation refetches the list: the old offset can
                // skip rows after a removal. If that refetch fails, a retry/remount is needed.
                page.links.hasMore = false;
              }
            });
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patch.undo);
        return;
      }
      if (!isAuctionListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      if (isNil(listToRemove) || endpointName === listToRemove) {
        const patchResult = mutationLifecycleApi.dispatch(
          sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            draft.data = draft.data.filter((vehicle) => {
              const adId = vehicle.auctionVehicle.adId;
              return isNil(adId) || !queryArgs.body.externalIds.includes(adId);
            });
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
      }
    });
}

export function updateAuctionVehicleSummaryInCaches(
  sourcingVehicleId: string,
  body: CreateUserVehicleSummaryRequestBody,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAuctionAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName === 'getAuctionUserList') {
        const patch = mutationLifecycleApi.dispatch(
          sourcingAuctionApi.util.updateQueryData('getAuctionUserList', originalArgs, (draft) => {
            draft.pages.forEach((page) =>
              page.data.forEach((vehicle) => {
                if (vehicle.auctionVehicle.adId === sourcingVehicleId) {
                  vehicle.vehicleSummary = {
                    id: vehicle.vehicleSummary?.id ?? '',
                    purchasePrice: body.purchasePrice ?? null,
                    sellingPrice: body.sellingPrice ?? null,
                    comment: body.comment ?? null,
                  };
                }
              })
            );
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patch.undo);
        return;
      }
      if (!isAuctionListEndpointName(endpointName)) {
        return;
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.data.forEach((vehicle) => {
            if (vehicle.auctionVehicle.adId === sourcingVehicleId) {
              vehicle.vehicleSummary = {
                id: vehicle.vehicleSummary?.id ?? '',
                purchasePrice: body.purchasePrice ?? null,
                sellingPrice: body.sellingPrice ?? null,
                comment: body.comment ?? null,
              };
            }
          });
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });

  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingAuctionDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'getSourcingAuctionDetail') {
        return;
      }

      if (originalArgs.externalId !== sourcingVehicleId) {
        return;
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.vehicleSummary = {
            id: draft.vehicleSummary?.id ?? '',
            purchasePrice: body.purchasePrice ?? null,
            sellingPrice: body.sellingPrice ?? null,
            comment: body.comment ?? null,
          };
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

export function updateAuctionDetailCacheByExternalId(
  externalId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>,
  updateFn: (draft: AuctionVehicleDetailResponseBody) => void
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingAuctionDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'getSourcingAuctionDetail') {
        return;
      }

      if (originalArgs.externalId !== externalId) {
        return;
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, updateFn)
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

/**
 * A cache priced in another currency, buyer country or strategy must keep its own numbers.
 * The All Offers list sends the buyer country in the filter body, user lists and detail as a
 * param. `list-by-ids` takes no margin, so a detail priced with a margin is left alone.
 */
const isSamePricingContext = (args: CachedAuctionArgs, context: AuctionPricingContext) =>
  args.currency === context.currency &&
  args.pricingStrategy === context.pricingStrategy &&
  (args.buyerCountry ?? args.body?.buyer?.country) === context.buyerCountry &&
  isNil(args.margin);

/**
 * Permanent (non-undo) overwrite of `auctionVehicle` in every cached auction list and detail
 * priced like `context`. User state (favourite, seen, inCart, vehicleSummary, ...) stays as
 * cached, so the poll cannot revert a pending optimistic update. Auctions missing from
 * `auctionVehicles` stay unchanged, and no row is added, removed or reordered.
 * A cache fetched after `requestedAt` already holds newer data than the poll, so it is skipped
 * until the next tick.
 */
export function patchAuctionVehiclesInCaches(
  auctionVehicles: AuctionVehicleResponseBody[],
  context: AuctionPricingContext,
  requestedAt: number,
  cacheApi: AuctionCacheApi
) {
  const freshByAdId = new Map<string, AuctionVehicleResponseBody>();
  auctionVehicles.forEach((auctionVehicle) => {
    if (isNotNil(auctionVehicle.adId)) {
      freshByAdId.set(auctionVehicle.adId, auctionVehicle);
    }
  });
  if (freshByAdId.size === 0) {
    return;
  }

  const {queries} = cacheApi.getState()[sourcingAuctionApi.reducerPath];
  // An infinite user-list cache has one fulfilledTimeStamp for all its pages. Loading a later
  // page after this poll started makes the whole entry newer, so skip it rather than overwrite
  // that page with older poll data. The next poll updates all previously loaded pages.
  const isNewerThanPoll = (queryCacheKey: string) =>
    (queries[queryCacheKey]?.fulfilledTimeStamp ?? 0) > requestedAt;

  sourcingAuctionApi.util
    .selectInvalidatedBy(cacheApi.getState(), [
      {type: 'SourcingAuctionAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs, queryCacheKey}) => {
      if (endpointName === 'getAuctionUserList') {
        if (!isSamePricingContext(originalArgs, context) || isNewerThanPoll(queryCacheKey)) {
          return;
        }
        cacheApi.dispatch(
          sourcingAuctionApi.util.updateQueryData('getAuctionUserList', originalArgs, (draft) => {
            draft.pages.forEach((page) => {
              page.data.forEach((vehicle) => {
                const adId = vehicle.auctionVehicle.adId;
                const freshVehicle = isNotNil(adId) ? freshByAdId.get(adId) : undefined;
                if (isNotNil(freshVehicle) && !equals(vehicle.auctionVehicle, freshVehicle)) {
                  vehicle.auctionVehicle = freshVehicle;
                }
              });
            });
          })
        );
        return;
      }
      if (
        !isAuctionListEndpointName(endpointName) ||
        !isSamePricingContext(originalArgs, context) ||
        isNewerThanPoll(queryCacheKey)
      ) {
        return;
      }

      cacheApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.data.forEach((vehicle) => {
            const adId = vehicle.auctionVehicle.adId;
            const freshVehicle = isNotNil(adId) ? freshByAdId.get(adId) : undefined;
            // Skipping equal data keeps the row reference, so unchanged rows do not re-render.
            if (isNotNil(freshVehicle) && !equals(vehicle.auctionVehicle, freshVehicle)) {
              vehicle.auctionVehicle = freshVehicle;
            }
          });
        })
      );
    });

  sourcingAuctionApi.util
    .selectInvalidatedBy(cacheApi.getState(), [{type: 'SourcingAuctionDetail'}])
    .forEach(({endpointName, originalArgs, queryCacheKey}) => {
      if (
        endpointName !== 'getSourcingAuctionDetail' ||
        !isSamePricingContext(originalArgs, context) ||
        isNewerThanPoll(queryCacheKey)
      ) {
        return;
      }

      // The detail is keyed by `externalId`, which is the auction's `adId`.
      const freshVehicle = freshByAdId.get(originalArgs.externalId);
      if (isNil(freshVehicle)) {
        return;
      }

      cacheApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          if (!equals(draft.auctionVehicle, freshVehicle)) {
            draft.auctionVehicle = freshVehicle;
          }
        })
      );
    });
}

export {sourcingAuctionApi};

export const {
  useGetSourcingAuctionListQuery,
  useGetAuctionUserListInfiniteQuery,
  useGetSourcingAuctionDetailQuery,
  useGetSourcingAuctionCountQuery,
  useAuctionBulkDeleteUserFavouriteMutation,
  useAuctionBulkCreateUserFavouriteMutation,
  useAuctionBulkDeleteUserComparisonMutation,
  useAuctionClearUserComparisonMutation,
  useAuctionBulkCreateUserComparisonMutation,
  useAuctionReorderUserComparisonMutation,
  useAuctionBulkDeleteUserHiddenMutation,
  useAuctionBulkCreateUserHiddenMutation,
  useAuctionBulkDeleteUserToBuyMutation,
  useAuctionBulkCreateUserToBuyMutation,
  useAuctionPushVehiclesToSaleMutation,
  useSourcingSetAuctionUserSeenMutation,
  useGetAuctionServerListQuery,
} = sourcingAuctionApi;
